import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * Konektory — admin API `/admin/api/v1/connectors` (API chain, JWT cookie, ROLE_ADMIN).
 * Admin twin integračního `/api/v1/connector-config`. Credentials jsou maskované
 * (`****`); při zápisu server maskované a (u credentials) prázdné hodnoty zachovává.
 */
export interface ConnectorConfig {
  connectorType: string;
  enabled: boolean;
  configVersion: number;
  config: Record<string, unknown>;
  credentials?: Record<string, unknown> | null;
  displayName?: string | null;
  description?: string | null;
}

export interface TestResult {
  success: boolean;
  message: string | null;
  responseTimeMs: number | null;
  connectorType: string;
  error: string | null;
  /** UP | DEGRADED | DOWN — DEGRADED = někdo odpověděl, ale ne cílový server (typicky proxy). */
  status?: "UP" | "DEGRADED" | "DOWN" | null;
  httpStatus?: number | null;
  /** `direct` / `via proxy host:port` — anglický text pro API klienty a logy. */
  route?: string | null;
  /** Anglický důvod pro API klienty; UI ho použije jen jako fallback k `detailCode`. */
  detail?: string | null;
  /** Strojový kód důvodu — UI ho vykreslí v jazyce operátora (viz `connectors.detail.*`). */
  detailCode?: ConnectorDetailCode | null;
  /** `host:port` proxy, přes kterou dotaz šel; `null` = napřímo. */
  proxyTarget?: string | null;
}

/** Klasifikované důvody stavu konektoru — zrcadlí `ConnectorHealth.DetailCode` na serveru. */
export type ConnectorDetailCode =
  | "PROBE_ENDPOINT_UNAVAILABLE"
  | "REQUIRES_AUTHENTICATION"
  | "NOT_FROM_TARGET_SERVER";

export interface UpdateConnectorBody {
  enabled: boolean;
  config: Record<string, unknown>;
  credentials?: Record<string, unknown> | null;
}

export function useConnectors() {
  return useQuery({
    queryKey: ["connector-config"],
    queryFn: () => apiFetch<ConnectorConfig[]>("/admin/api/v1/connectors"),
  });
}

/**
 * `AuthServerType` konektorů, které jsou v TOMHLE buildu a zapnuté — jediný zdroj
 * pravdy pro všechny nabídky auth serverů v UI.
 *
 * <p>Nikdy nevypisuj typy natvrdo: zákazník dostává build jen se svými konektory
 * a nabídka se všemi typy (KB_CAAS / CSOB_ECHANNELS / ECOBRA / …) mu ukazuje cizí
 * banky a volby, které v jeho instalaci nefungují. `ETTS` do nabídky nepatří vůbec —
 * je to interní typ pro logování příchozích požadavků, ne aktivační cíl.
 */
export function useAuthServerTypes(): string[] {
  const { data } = useConnectors();
  return (data ?? []).filter((c) => c.enabled).map((c) => c.connectorType);
}

/** Test připojení konektoru (POST `/{type}/test`). */
export function useTestConnection() {
  return useMutation({
    mutationFn: (connectorType: string) =>
      apiFetch<TestResult>(`/admin/api/v1/connectors/${encodeURIComponent(connectorType)}/test`, { method: "POST" }),
  });
}

/** Uložení konfigurace konektoru (PUT). Server merguje (skip `****`, prázdné credentials = beze změny). */
export function useUpdateConnector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ connectorType, body }: { connectorType: string; body: UpdateConnectorBody }) =>
      apiFetch<ConnectorConfig>(`/admin/api/v1/connectors/${encodeURIComponent(connectorType)}`, {
        method: "PUT",
        json: body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connector-config"] });
    },
  });
}

/** Reset konektoru na výchozí hodnoty (POST `/{type}/reset`). */
export function useResetConnector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (connectorType: string) =>
      apiFetch<ConnectorConfig>(`/admin/api/v1/connectors/${encodeURIComponent(connectorType)}/reset`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connector-config"] }),
  });
}
